/**
 *
 * @Copyright 2024 UNLOCKIT DECENTRALIZATION, LDA
 * Development by VOID Software, SA
 *
 */

import {
    FunctionComponent, useEffect, useMemo, useState,
} from 'react';
import { WorkflowStateEnum, WorkflowType, WorkflowsFilterParams } from '../../../types/workflows';
import { TranslationContext, withTranslationContext } from '../../controllers/TranslationContext';
import { FormField } from '../FormField';
import { DatePicker } from '../DatePicker';
import { RadioSelect } from '../RadioSelect';
import Button from '../Button';
import { ButtonVariant, OptionCheckboxGroupField, OrderOptions } from '../../../types/general';
import { FormSelectField } from '../FormSelectField';
import { OrganizationsContext, withOrganizationsContext } from '../../controllers/OrganizationsContext';
import { CheckboxGroupWithSearch } from '../CheckboxGroupWithSearch';

type OwnProps = TranslationContext & OrganizationsContext & {
    currentFilters: WorkflowsFilterParams;
    filterMemberOptions: (search?: string) => void;
    filteredMemberOptions: OptionCheckboxGroupField[];
    isLoadingMembers?: boolean;
    onSubmit: (filters: WorkflowsFilterParams) => void;
    onClean: () => void;
};

const WorkflowsFiltersFormBase: FunctionComponent<OwnProps> = (props: OwnProps) => {
    const {
        t,
        currentFilters,
        filterMemberOptions,
        filteredMemberOptions,
        isLoadingMembers = false,
        onSubmit,
        onClean,
        organizationSelected,
    } = props;
    const [filters, setFilters] = useState<WorkflowsFilterParams>(currentFilters);

    const transactionTypeOptions = [
        { value: WorkflowType.SELL, label: t('workflows.list.filters.transactionTypeOptions.SELL') },
        { value: WorkflowType.RENTAL, label: t('workflows.list.filters.transactionTypeOptions.RENTAL') },
    ];

    const sellStatusOptions = [
        { value: WorkflowStateEnum.SELL_RAISING, label: t('workflows.view.SELL.steps.SELL_RAISING') },
        { value: WorkflowStateEnum.SELL_DOCUMENTS_APPROVAL, label: t('workflows.view.SELL.steps.SELL_DOCUMENTS_APPROVAL') },
        { value: WorkflowStateEnum.SELL_PENDING_APPROVAL, label: t('workflows.view.SELL.steps.SELL_PENDING_APPROVAL') },
        { value: WorkflowStateEnum.SELL_NEGOTIATION, label: t('workflows.view.SELL.steps.SELL_NEGOTIATION') },
        { value: WorkflowStateEnum.SELL_BUYING_PROMISSORY_AGREEMENT, label: t('workflows.view.SELL.steps.SELL_BUYING_PROMISSORY_AGREEMENT') },
        { value: WorkflowStateEnum.SELL_DEED, label: t('workflows.view.SELL.steps.SELL_DEED') },
        { value: WorkflowStateEnum.SELL_CANCELED, label: t('workflows.view.SELL.steps.SELL_CANCELED') },
        { value: WorkflowStateEnum.SELL_CLOSED, label: t('workflows.view.SELL.steps.SELL_CLOSED') },
    ];
    const rentalStatusOptions = [
        { value: WorkflowStateEnum.RENTAL_RAISING, label: t('workflows.view.RENTAL.steps.RENTAL_RAISING') },
        { value: WorkflowStateEnum.RENTAL_DOCUMENTS_APPROVAL, label: t('workflows.view.RENTAL.steps.RENTAL_DOCUMENTS_APPROVAL') },
        { value: WorkflowStateEnum.RENTAL_PENDING_APPROVAL, label: t('workflows.view.RENTAL.steps.RENTAL_PENDING_APPROVAL') },
        { value: WorkflowStateEnum.RENTAL_LISTED, label: t('workflows.view.RENTAL.steps.RENTAL_LISTED') },
        { value: WorkflowStateEnum.RENTAL_NEGOTIATION, label: t('workflows.view.RENTAL.steps.RENTAL_NEGOTIATION') },
        { value: WorkflowStateEnum.RENTAL_PROPOSAL_APPROVED, label: t('workflows.view.RENTAL.steps.RENTAL_PROPOSAL_APPROVED') },
        { value: WorkflowStateEnum.RENTAL_CANCELED, label: t('workflows.view.RENTAL.steps.RENTAL_CANCELED') },
        { value: WorkflowStateEnum.RENTAL_RENTED, label: t('workflows.view.RENTAL.steps.RENTAL_RENTED') },
    ];
    
    const orderOptions = [
        { value: OrderOptions.ASCENDING, label: t('workflows.list.filters.orderAscending') },
        { value: OrderOptions.DESCENDING, label: t('workflows.list.filters.orderDescending') },
    ];

    useEffect(() => {
        setFilters({ ...currentFilters });
    }, [currentFilters]);
    
    const memberOptions = useMemo(() => {
        return filteredMemberOptions.map((option) => (
            {
                ...option,
                value: !!filters.organizationMemberIds && filters.organizationMemberIds.includes(String(option.id)),
            }
        ));
    },
    [filters.organizationMemberIds, filteredMemberOptions]);
    /**
     * Update the filters field change
     *
     * @remarks
     * Verify the field changed and update in filters state
     */
    const onFieldsChange = (name: string, value: string | number | unknown) => {
        setFilters({ ...filters, [name]: value });
    };

    /**
     * Update the filters workflowType change
     *
     * @remarks
     * Verify the field changed and update in filters state
     */
    const onWorkflowTypeChange = (name: string, value: string | number | unknown) => {
        setFilters((prevFields) => ({
            ...prevFields,
            [name]: value,
            transactionStatus: undefined,
        }));
    };

    /**
    * Handle Date Change Function
    *
    * @remarks
    * Handle Date Picker component date value change
    */
    const handleDateChange = (date: Date | null, field: string) => {
        if (!date || isNaN(date.getTime())) return;
        setFilters({
            ...filters,
            [field]: date.toISOString(),
        });
    };

    /**
     * Update checkbox group field
     *
     * @remarks
     * Verify the field changed and update in filters state
     */
    const onCheckboxGroupChange = (id: number | string, checked: boolean) => {
        let selectedMemberIds = filters.organizationMemberIds ?? [];
        if (checked) {
            selectedMemberIds = [...selectedMemberIds, String(id)];
        } else {
            selectedMemberIds = selectedMemberIds.filter((item) => item !== String(id));
        }

        setFilters({ ...filters, organizationMemberIds: selectedMemberIds });
    };

    return (
        <form className="filters-form" data-testid="filters-form">
            <div className="filters-form__filters">
                <div className="filters-form__filters__order" data-testid="form-order">
                    <FormSelectField
                        name="_sort"
                        label={t('workflows.list.filters.order')}
                        options={orderOptions}
                        value={filters._sort}
                        onChange={onFieldsChange}
                        fullWidth
                    />
                    <div className="separator" />
                </div>
                <FormField
                    name="externalId"
                    label={t('workflows.list.filters.externalId')}
                    placeholder={t('workflows.list.filters.externalIdPlaceholder')}
                    value={filters.externalId}
                    onChange={onFieldsChange}
                    extraClasses="bg-gray-filled filter-externalId"
                    fullWidth
                />
                <div className="separator" />
                <RadioSelect
                    name="transactionType"
                    label={t('workflows.list.filters.transactionType')}
                    value={filters.transactionType}
                    options={transactionTypeOptions}
                    onChange={onWorkflowTypeChange}
                />
                {filters.transactionType && (
                    <>
                        <div className="separator" />
                        <RadioSelect
                            name="transactionStatus"
                            label={t('workflows.list.filters.transactionStatus')}
                            value={filters.transactionStatus}
                            options={filters.transactionType === WorkflowType.RENTAL ? rentalStatusOptions : sellStatusOptions}
                            onChange={onFieldsChange}
                            isCollapsible
                        />
                        <div className="separator" />
                    </>
                )}
                {organizationSelected?.organization?.id && (
                    <>
                        <CheckboxGroupWithSearch
                            name="organizationMemberIds"
                            label={t('workflows.list.filters.organizationMemberIds')}
                            searchPlaceholder={t('workflows.list.filters.organizationMemberIdsSearchPlaceholder')}
                            options={memberOptions}
                            onChange={onCheckboxGroupChange}
                            onSearch={filterMemberOptions}
                            isLoading={isLoadingMembers}
                        />
                        <div className="separator" />
                    </>
                )}
                <DatePicker
                    name="from"
                    label={t('workflows.list.filters.from')}
                    format="DD/MM/YYYY"
                    value={filters.from ? String(filters.from) : undefined}
                    onChange={handleDateChange}
                    extraClasses="bg-gray-filled full-width workflow-from"
                />
                <DatePicker
                    name="to"
                    label={t('workflows.list.filters.to')}
                    format="DD/MM/YYYY"
                    value={filters.to ? String(filters.to) : undefined}
                    onChange={handleDateChange}
                    extraClasses="bg-gray-filled full-width workflow-to"
                />
                <div className="separator" />
                <FormField
                    name="email"
                    label={t('workflows.list.filters.email')}
                    placeholder={t('workflows.list.filters.emailPlaceholder')}
                    value={filters.email}
                    onChange={onFieldsChange}
                    extraClasses="bg-gray-filled filter-email"
                    fullWidth
                />
            </div>
            <div className="filters-form__buttons">
                <Button
                    variant={ButtonVariant.Curved}
                    extraClasses="secondary clean-btn"
                    onClick={onClean}
                    testId="clean-btn"
                >
                    {t('workflows.list.filters.cleanAll')}
                </Button>
                <Button
                    variant={ButtonVariant.Curved}
                    onClick={() => onSubmit(filters)}
                    testId="submit-btn"
                >
                    {t('general.applyFilters')}
                </Button>
            </div>
        </form>
    );
};

export const WorkflowsFiltersForm = withTranslationContext(withOrganizationsContext(WorkflowsFiltersFormBase));
